import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ROLES, ROLE_LABELS, DEPARTMENTS, TASK_TYPES, TASK_PRIORITIES } from "@/constants";
import { toast } from "sonner";
import { Trash, Plus, EnvelopeSimple, CheckCircle, XCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { SapBulkPush } from "@/components/Settings/SapBulkPush";

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [approveForm, setApproveForm] = useState({});
  const [templates, setTemplates] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "team_member", department: "" });
  const [newTemplate, setNewTemplate] = useState({ name: "", title: "", task_type: "Other", priority: "Medium", department: "" });

  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data));
  const loadPending = () => api.get("/users/pending").then((r) => setPending(r.data));
  const loadTemplates = () => api.get("/task-templates").then((r) => setTemplates(r.data));
  const loadEmailStatus = () => api.get("/email/status").then((r) => setEmailStatus(r.data));

  useEffect(() => { loadUsers(); loadPending(); loadTemplates(); loadEmailStatus(); }, []);

  const approveUser = async (id) => {
    const form = approveForm[id] || {};
    try {
      await api.post(`/users/${id}/approve`, { role: form.role || "team_member", department: form.department || null });
      toast.success("User approved");
      loadPending(); loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Failed to approve"); }
  };

  const rejectUser = async (id) => {
    try {
      await api.post(`/users/${id}/reject`);
      toast.success("Access request declined");
      loadPending(); loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Failed to reject"); }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const { data } = await api.post("/email/test");
      if (data.success) toast.success("Test email sent — check your inbox");
      else toast.error(data.detail || "Test email failed");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Test email failed");
    } finally {
      setTestingEmail(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", newUser);
      toast.success("User created");
      setNewUser({ name: "", email: "", password: "", role: "team_member", department: "" });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Failed to create user"); }
  };

  const updateUserRole = async (id, role) => { await api.put(`/users/${id}`, { role }); loadUsers(); };
  const [syncing, setSyncing] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [keyLabel, setKeyLabel] = useState("");
  const [newKey, setNewKey] = useState(null);
  const loadApiKeys = () => api.get("/external-keys").then((r) => setApiKeys(r.data)).catch(() => {});
  useEffect(() => { loadApiKeys(); }, []);
  const createApiKey = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/external-keys", { label: keyLabel });
      setNewKey(data.api_key);
      setKeyLabel("");
      loadApiKeys();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Failed to create key"); }
  };
  const revokeApiKey = async (id) => {
    await api.delete(`/external-keys/${id}`);
    toast.success("Key revoked");
    loadApiKeys();
  };
  const [webhooks, setWebhooks] = useState([]);
  const [hookUrl, setHookUrl] = useState("");
  const [hookSecret, setHookSecret] = useState(null);
  const loadWebhooks = () => api.get("/webhooks").then((r) => setWebhooks(r.data)).catch(() => {});
  useEffect(() => { loadWebhooks(); }, []);
  const createWebhook = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/webhooks", { url: hookUrl });
      setHookSecret(data.secret);
      setHookUrl("");
      loadWebhooks();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Failed to add webhook"); }
  };
  const testWebhook = async (id) => {
    try {
      const { data } = await api.post(`/webhooks/${id}/test`);
      toast[data.last_status === 200 ? "success" : "warning"](`Ping sent — endpoint responded: ${data.last_status}`);
      loadWebhooks();
    } catch { toast.error("Test failed"); }
  };
  const deleteWebhook = async (id) => { await api.delete(`/webhooks/${id}`); loadWebhooks(); };
  const [adminLimits, setAdminLimits] = useState(null);
  const loadAdminLimits = () => api.get("/users/admin-limits").then((r) => setAdminLimits(r.data)).catch(() => setAdminLimits(null));
  useEffect(() => { loadAdminLimits(); }, []);
  const saveAdminLimit = async (email, raw) => {
    const limit = raw === "" ? null : Math.max(0, Number(raw) || 0);
    try {
      await api.put("/users/admin-limits", { email, limit });
      toast.success(limit === null ? `${email} — quota removed (unlimited)` : `${email} — quota set to ${limit}`);
      loadAdminLimits();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Failed to update quota"); }
  };
  const syncFromErp = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/users/sync-phones");
      toast.success(`ERP sync done — ${data.dept_updated_count} departments and ${data.updated_count} phones updated`);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "ERP sync failed"); }
    finally { setSyncing(false); }
  };
  const updateUserDept = async (id, department) => {
    try {
      await api.put(`/users/${id}`, { department });
      toast.success("Department updated");
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Failed to update department"); }
  };
  const deleteUser = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Failed to delete user"); }
  };

  const createTemplate = async (e) => {
    e.preventDefault();
    await api.post("/task-templates", newTemplate);
    setNewTemplate({ name: "", title: "", task_type: "Other", priority: "Medium", department: "" });
    loadTemplates();
  };
  const deleteTemplate = async (id) => { await api.delete(`/task-templates/${id}`); loadTemplates(); };

  return (
    <div data-testid="settings-page">
      <span className="label-caps">Admin</span>
      <h1 className="text-2xl font-display font-extrabold mt-1 mb-4">Settings</h1>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger data-testid="settings-tab-users" value="users">Users</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-templates" value="templates">Task Templates</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-email" value="email">Email</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-api" value="api">API Access</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-sap" value="sap">SAP</TabsTrigger>
        </TabsList>

        <TabsContent value="sap">
          <SapBulkPush />
        </TabsContent>

        <TabsContent value="users">
          <div className="flex justify-end mb-3">
            <button data-testid="sync-erp-btn" onClick={syncFromErp} disabled={syncing}
              className="btn-outline-navy flex items-center gap-2 !py-1.5 !px-3 text-xs disabled:opacity-50">
              <ArrowsClockwise size={13} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing from ERP..." : "Sync Phones & Departments from ERP"}
            </button>
          </div>
          {pending.length > 0 && (
            <div className="card-flat p-4 mb-4" style={{ borderColor: "hsl(var(--accent) / 0.5)" }} data-testid="pending-requests-section">
              <span className="label-caps">Pending Access Requests ({pending.length})</span>
              <div className="divide-y divide-border mt-2">
                {pending.map((p) => (
                  <div key={p.id} data-testid={`pending-request-row-${p.id}`} className="py-3 flex items-center justify-between flex-wrap gap-2">
                    <div><div className="font-medium text-sm">{p.name}</div><div className="text-xs text-muted-foreground">{p.email}</div></div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={approveForm[p.id]?.role || "team_member"} onValueChange={(v) => setApproveForm((s) => ({ ...s, [p.id]: { ...s[p.id], role: v } }))}>
                        <SelectTrigger data-testid={`pending-role-select-${p.id}`} className="w-36 h-8" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={approveForm[p.id]?.department || ""} onValueChange={(v) => setApproveForm((s) => ({ ...s, [p.id]: { ...s[p.id], department: v } }))}>
                        <SelectTrigger data-testid={`pending-department-select-${p.id}`} className="w-40 h-8" style={{ borderRadius: "2px" }}><SelectValue placeholder="Dept (optional)" /></SelectTrigger>
                        <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                      <button data-testid={`approve-user-btn-${p.id}`} onClick={() => approveUser(p.id)} className="btn-navy !py-1.5 !px-3 flex items-center gap-1"><CheckCircle size={13} />Approve</button>
                      <button data-testid={`reject-user-btn-${p.id}`} onClick={() => rejectUser(p.id)} className="p-2 hover:bg-destructive/10 text-destructive" style={{ borderRadius: "2px" }}><XCircle size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {adminLimits && (
            <div className="card-flat p-4 mb-4" data-testid="admin-quotas-section">
              <span className="label-caps">Admin User Quotas <span className="normal-case font-normal text-muted-foreground">(master account only — blank = unlimited)</span></span>
              <div className="divide-y divide-border mt-2">
                {adminLimits.map((a) => (
                  <div key={a.email} data-testid={`quota-row-${a.email}`} className="py-2.5 flex items-center justify-between flex-wrap gap-2">
                    <div><div className="font-medium text-sm">{a.name}</div><div className="text-xs text-muted-foreground">{a.email} · {a.used} users created</div></div>
                    <Input type="number" min={0} defaultValue={a.limit ?? ""} placeholder="∞"
                      data-testid={`quota-input-${a.email}`}
                      className="w-24 h-8" style={{ borderRadius: "2px" }}
                      onBlur={(e) => { const v = e.target.value; if (v !== String(a.limit ?? "")) saveAdminLimit(a.email, v); }} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <form onSubmit={createUser} className="card-flat p-4 grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
            <Input data-testid="new-user-name-input" placeholder="Name" required value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} style={{ borderRadius: "2px" }} />
            <Input data-testid="new-user-email-input" placeholder="Email" type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} style={{ borderRadius: "2px" }} />
            <Input data-testid="new-user-password-input" placeholder="Password" type="password" required value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} style={{ borderRadius: "2px" }} />
            <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
              <SelectTrigger data-testid="new-user-role-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={newUser.department} onValueChange={(v) => setNewUser({ ...newUser, department: v })}>
              <SelectTrigger data-testid="new-user-department-select" style={{ borderRadius: "2px" }}><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <button data-testid="create-user-btn" className="btn-navy flex items-center justify-center gap-1"><Plus size={13} />Add User</button>
          </form>
          <div className="card-flat divide-y divide-border">
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="p-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-medium text-sm flex items-center gap-1.5">
                    {u.name}
                    {u.status && u.status !== "active" && (
                      <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-muted text-muted-foreground" style={{ borderRadius: "2px" }} data-testid={`user-status-badge-${u.id}`}>{u.status}</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{u.email} · {u.department || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={u.department || ""} onValueChange={(v) => updateUserDept(u.id, v)}>
                    <SelectTrigger data-testid={`user-department-select-${u.id}`} className="w-44 h-8" style={{ borderRadius: "2px" }}><SelectValue placeholder="Department" /></SelectTrigger>
                    <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={u.role || "team_member"} onValueChange={(v) => updateUserRole(u.id, v)}>
                    <SelectTrigger data-testid={`user-role-select-${u.id}`} className="w-40 h-8" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                  </Select>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-user-btn-${u.id}`} className="p-2 hover:bg-destructive/10 text-destructive" style={{ borderRadius: "2px" }}><Trash size={14} /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`delete-user-dialog-${u.id}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                        <AlertDialogDescription>This removes their account and login access. This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`delete-user-cancel-btn-${u.id}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`delete-user-confirm-btn-${u.id}`} onClick={() => deleteUser(u.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete User</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <form onSubmit={createTemplate} className="card-flat p-4 grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Input data-testid="new-template-name-input" placeholder="Template name" required value={newTemplate.name} onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })} style={{ borderRadius: "2px" }} />
            <Input data-testid="new-template-title-input" placeholder="Default task title" required value={newTemplate.title} onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })} style={{ borderRadius: "2px" }} />
            <Select value={newTemplate.task_type} onValueChange={(v) => setNewTemplate({ ...newTemplate, task_type: v })}>
              <SelectTrigger data-testid="new-template-type-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
              <SelectContent>{TASK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={newTemplate.department} onValueChange={(v) => setNewTemplate({ ...newTemplate, department: v })}>
              <SelectTrigger data-testid="new-template-department-select" style={{ borderRadius: "2px" }}><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <button data-testid="create-template-btn" className="btn-navy flex items-center justify-center gap-1"><Plus size={13} />Add Template</button>
          </form>
          <div className="card-flat divide-y divide-border">
            {templates.map((t) => (
              <div key={t.id} data-testid={`template-row-${t.id}`} className="p-3 flex items-center justify-between">
                <div><div className="font-medium text-sm">{t.name}</div><div className="text-xs text-muted-foreground">{t.title} · {t.task_type} · {t.department || "Any dept"}</div></div>
                <button data-testid={`delete-template-btn-${t.id}`} onClick={() => deleteTemplate(t.id)} className="p-2 hover:bg-destructive/10 text-destructive" style={{ borderRadius: "2px" }}><Trash size={14} /></button>
              </div>
            ))}
            {templates.length === 0 && <div className="p-4 text-sm font-mono text-muted-foreground">&gt; no templates saved yet</div>}
          </div>
        </TabsContent>

        <TabsContent value="email">
          <div className="card-flat p-5 max-w-lg">
            <span className="label-caps"><EnvelopeSimple size={12} />Microsoft Graph Email</span>
            <div className="flex items-center gap-2 mt-3 text-sm" data-testid="email-config-status">
              {emailStatus?.configured ? (
                <><CheckCircle size={16} className="text-green-600" /> Connected — alerts send from each user's own mailbox</>
              ) : (
                <><XCircle size={16} className="text-destructive" /> Not connected yet — add GRAPH_TENANT_ID, GRAPH_CLIENT_ID, GRAPH_CLIENT_SECRET to backend/.env</>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              When connected, task-assignment emails are sent from the assigner's own Microsoft 365 mailbox
              and include a one-click "Mark Complete" link — no reply parsing needed.
            </p>
            <button data-testid="send-test-email-btn" onClick={sendTestEmail} disabled={testingEmail || !emailStatus?.configured} className="btn-navy mt-4">
              {testingEmail ? "Sending..." : "Send Test Email to Myself"}
            </button>
          </div>
        </TabsContent>

        <TabsContent value="api">
          <div className="card-flat p-5 max-w-3xl" data-testid="api-access-section">
            <span className="label-caps">External API Keys</span>
            <p className="text-xs text-muted-foreground mt-2 mb-4">
              Generate a key to let external apps read project data (names, owners, status, tasks, phases, milestones) — read-only.
              Endpoints: <code className="font-mono bg-muted px-1">GET /api/external/v1/projects</code> and{" "}
              <code className="font-mono bg-muted px-1">GET /api/external/v1/projects/&#123;id&#125;</code> with header{" "}
              <code className="font-mono bg-muted px-1">X-API-Key: your_key</code>
            </p>
            {newKey && (
              <div className="p-3 mb-4 bg-green-50 border border-green-300 text-green-900 dark:bg-green-500/10 dark:border-green-500/40 dark:text-green-300 text-sm" style={{ borderRadius: "2px" }} data-testid="new-api-key-box">
                <div className="font-semibold text-xs uppercase mb-1">Copy this key now — it won't be shown again</div>
                <code className="font-mono text-xs break-all select-all">{newKey}</code>
              </div>
            )}
            <form onSubmit={createApiKey} className="flex gap-2 mb-4">
              <Input data-testid="api-key-label-input" placeholder="Key label (e.g. Inventory App)" required value={keyLabel}
                onChange={(e) => setKeyLabel(e.target.value)} className="max-w-xs" style={{ borderRadius: "2px" }} />
              <button data-testid="generate-api-key-btn" className="btn-navy">Generate Key</button>
            </form>
            <div className="divide-y divide-border">
              {apiKeys.map((k) => (
                <div key={k.id} className="py-2.5 flex items-center justify-between" data-testid={`api-key-row-${k.id}`}>
                  <div>
                    <div className="font-medium text-sm">{k.label} <code className="font-mono text-xs text-muted-foreground ml-1">{k.prefix}…</code>
                      {k.revoked && <span className="ml-2 text-[10px] font-mono uppercase px-1.5 py-0.5 bg-destructive/10 text-destructive" style={{ borderRadius: "2px" }}>Revoked</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">by {k.created_by_name} · {k.last_used_at ? `last used ${new Date(k.last_used_at).toLocaleString()}` : "never used"}</div>
                  </div>
                  {!k.revoked && (
                    <button data-testid={`revoke-api-key-btn-${k.id}`} onClick={() => revokeApiKey(k.id)}
                      className="text-xs px-2.5 py-1 border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground" style={{ borderRadius: "2px" }}>
                      Revoke
                    </button>
                  )}
                </div>
              ))}
              {apiKeys.length === 0 && <p className="text-xs font-mono text-muted-foreground">&gt; no API keys yet</p>}
            </div>
          </div>

          <div className="card-flat p-5 max-w-3xl mt-4" data-testid="webhooks-section">
            <span className="label-caps">Webhooks (Instant Push)</span>
            <p className="text-xs text-muted-foreground mt-2 mb-4">
              We POST a signed JSON payload to your URL the moment anything changes. Events:{" "}
              <code className="font-mono bg-muted px-1">project.created/updated/deleted/completed</code>,{" "}
              <code className="font-mono bg-muted px-1">task.created/updated/completed/deleted</code>,{" "}
              <code className="font-mono bg-muted px-1">milestone.achieved</code>.
              Verify authenticity via <code className="font-mono bg-muted px-1">X-Webhook-Signature</code> (HMAC-SHA256 of raw body with your secret).
            </p>
            {hookSecret && (
              <div className="p-3 mb-4 bg-green-50 border border-green-300 text-green-900 dark:bg-green-500/10 dark:border-green-500/40 dark:text-green-300 text-sm" style={{ borderRadius: "2px" }} data-testid="new-webhook-secret-box">
                <div className="font-semibold text-xs uppercase mb-1">Signing secret — copy now, shown only once</div>
                <code className="font-mono text-xs break-all select-all">{hookSecret}</code>
              </div>
            )}
            <form onSubmit={createWebhook} className="flex gap-2 mb-4">
              <Input data-testid="webhook-url-input" placeholder="https://your-app.com/webhook" required value={hookUrl}
                onChange={(e) => setHookUrl(e.target.value)} className="max-w-md" style={{ borderRadius: "2px" }} />
              <button data-testid="add-webhook-btn" className="btn-navy">Add Webhook</button>
            </form>
            <div className="divide-y divide-border">
              {webhooks.map((h) => (
                <div key={h.id} className="py-2.5 flex items-center justify-between gap-2" data-testid={`webhook-row-${h.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{h.url}</div>
                    <div className="text-xs text-muted-foreground">
                      {h.last_triggered_at ? `last delivery: ${new Date(h.last_triggered_at).toLocaleString()} → ${h.last_status}` : "never triggered"}
                      {h.failure_count > 0 && <span className="text-destructive ml-2">{h.failure_count} failures</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`test-webhook-btn-${h.id}`} onClick={() => testWebhook(h.id)}
                      className="text-xs px-2.5 py-1 border border-border hover:bg-muted" style={{ borderRadius: "2px" }}>Send Test</button>
                    <button data-testid={`delete-webhook-btn-${h.id}`} onClick={() => deleteWebhook(h.id)}
                      className="text-xs px-2.5 py-1 border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground" style={{ borderRadius: "2px" }}>Delete</button>
                  </div>
                </div>
              ))}
              {webhooks.length === 0 && <p className="text-xs font-mono text-muted-foreground">&gt; no webhooks yet</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
